import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { initialsOf } from '../../components/common';
import { relativeTime } from '../ally/ChatList';
import { moderationRefusal, type FeedComment } from '../../lib/backend/types';

/**
 * The comments on one post, as a bottom sheet.
 *
 * A SHEET RATHER THAN A ROUTE, because a comment is read against the post it
 * answers. Navigating away to a screen would take the post off-screen and make
 * "what is this replying to?" a memory exercise; the sheet leaves the card
 * visible behind the scrim.
 *
 * NEWEST FIRST, COMPOSER AT THE BOTTOM — deliberately not the chat arrangement
 * (oldest first, scrolled to the end). A feed comment thread is read like a
 * feed: the most recent thing is the thing you have not seen. Paging is manual
 * for the same reason FeedScreen's is: every page is a paid-for read.
 *
 * NO NEW CSS. Everything here is an existing class (`card`, `chip`, `btn`,
 * `input`, `row`, `col`, `spread`, `tiny`, `small`, `muted`, `faint`) plus
 * inline layout keyed off the existing custom properties — `--chrome` for the
 * sheet surface, `--scrim` for the backdrop, `--safe-bottom` so the composer
 * clears the gesture bar on Android.
 */

/* The page SIZE is deliberately not mirrored here. It is fixed server-side and
   not client-tunable — `listComments` takes a cursor and nothing else — so a
   constant in this file could only ever be a second number to keep in step
   with the first, and would be wrong silently if the server's ever changed. */

/** Matches FEED_COMMENT_TEXT_MAX. The server re-checks it. */
const TEXT_MAX = 500;

/** Show the counter only once it starts to matter, as the post composer does. */
const COUNTER_FROM = TEXT_MAX - 100;

export function CommentsSheet({
  postId,
  postAuthorUid,
  onClose,
  onCountChange,
}: {
  postId: string;
  /** Used to grant the post's owner deletion of comments the server has not paged in yet. */
  postAuthorUid: string;
  onClose(): void;
  /** Called with the signed delta (+1 / -1) so the card's count tracks the sheet. */
  onCountChange(delta: number): void;
}) {
  const { t } = useI18n();
  const app = useApp();
  const [comments, setComments] = useState<FeedComment[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'danger' | 'info'; text: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const me = app.user?.uid ?? '';

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const page = await app.backend.listComments(postId);
      setComments(page.comments);
      setCursor(page.cursor);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [app.backend, postId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Escape closes, because the sheet covers the screen and the only other way
  // out is a tap target someone using a keyboard may never reach.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await app.backend.listComments(postId, cursor);
      // Deduped on id: a comment deleted between two pages shifts the window,
      // and the same row twice would give React duplicate keys.
      setComments((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...page.comments.filter((c) => !seen.has(c.id))];
      });
      setCursor(page.cursor);
    } catch {
      setNotice({ tone: 'danger', text: t('feed.comments.loadFailed') });
    } finally {
      setLoadingMore(false);
    }
  };

  const submit = async () => {
    const text = draft.trim();
    if (!text || posting) return;
    if (text.length > TEXT_MAX) {
      setNotice({ tone: 'danger', text: t('feed.comments.tooLong') });
      return;
    }
    setPosting(true);
    setNotice(null);
    try {
      const receipt = await app.backend.addComment(postId, text);
      /* The callable returns a RECEIPT, not the row — see the note on
         addComment in lib/backend/types.ts. The row is assembled from the
         receipt plus what this client already knows for certain about itself:
         its own display name and its own uid. `createdAtISO` is the only
         approximation (the server stamped its own, milliseconds earlier), and
         it is corrected the next time the sheet opens. Nothing here invents a
         fact about another person. */
      const mine: FeedComment = {
        id: receipt.commentId,
        uid: me,
        authorName: app.user?.displayName ?? '',
        text,
        createdAtISO: new Date().toISOString(),
        moderation: receipt.moderation,
        mine: true,
        canDelete: true,
      };
      setComments((prev) => [mine, ...prev]);
      onCountChange(1);
      setDraft('');
      inputRef.current?.focus();
    } catch (e) {
      // The same two refusals a post and a message already show, in the same
      // words — one rule explained once, wherever you meet it.
      const refusal = moderationRefusal(e);
      if (refusal === 'blocked') {
        setNotice({ tone: 'danger', text: t('msg.moderation.blocked') });
      } else if (refusal === 'unavailable') {
        setNotice({ tone: 'info', text: t('msg.moderation.unavailable') });
      } else {
        setNotice({ tone: 'danger', text: t('feed.comments.failed') });
      }
      // The draft is deliberately NOT cleared: a refused comment was never
      // written, so the words are still the author's and still theirs to edit.
    } finally {
      setPosting(false);
    }
  };

  const remove = async (comment: FeedComment) => {
    const before = comments;
    // Optimistic, and reverted below — a comment that lingers after you press
    // Delete reads as a failure even when the call is merely slow.
    setComments((prev) => prev.filter((c) => c.id !== comment.id));
    onCountChange(-1);
    try {
      await app.backend.deleteComment(postId, comment.id);
    } catch {
      setComments(before);
      onCountChange(1);
      setNotice({ tone: 'danger', text: t('feed.comments.deleteFailed') });
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        background: 'var(--scrim)',
      }}
      // The backdrop closes the sheet. The sheet itself stops the click, so a
      // tap inside it never dismisses the thing being tapped.
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('feed.comments.title')}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--chrome)',
          borderTop: '1px solid var(--border)',
          borderTopLeftRadius: 'var(--radius)',
          borderTopRightRadius: 'var(--radius)',
          maxHeight: '82vh',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 'var(--safe-bottom)',
        }}
      >
        <div
          className="spread"
          style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--border)' }}
        >
          <span className="h3">{t('feed.comments.title')}</span>
          <button className="chip" onClick={onClose}>
            {t('feed.comments.close')}
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '12px 16px', flex: 1 }}>
          {loading ? (
            <div style={{ display: 'grid', placeItems: 'center', padding: '28px 0' }}>
              <div className="spinner" />
            </div>
          ) : failed ? (
            <div className="banner banner--danger small spread">
              <span>{t('feed.comments.loadFailed')}</span>
              <button className="chip" onClick={() => void load()}>
                {t('common.retry')}
              </button>
            </div>
          ) : comments.length === 0 ? (
            <div className="tiny faint center" style={{ padding: '22px 0' }}>
              {t('feed.comments.empty')}
            </div>
          ) : (
            <div className="col" style={{ gap: 14 }}>
              {comments.map((c) => (
                <div key={c.id} className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
                  <div className="avatar" aria-hidden>
                    {initialsOf(c.authorName || '?')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="spread">
                      <span className="small" style={{ fontWeight: 600 }}>
                        {c.authorName}
                      </span>
                      <span className="tiny faint">{relativeTime(c.createdAtISO, t)}</span>
                    </div>
                    <div
                      className="small"
                      style={{
                        marginTop: 3,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {c.text}
                    </div>
                    {/* Stated, not hidden: `unchecked` means no provider
                        looked. It is a fact about our pipeline, never an
                        insinuation about the person who wrote the comment. */}
                    {c.moderation === 'unchecked' && (
                      <div className="tiny faint" style={{ marginTop: 3 }}>
                        {t('feed.comments.unchecked')}
                      </div>
                    )}
                    {(c.canDelete || c.uid === me || postAuthorUid === me) && (
                      <button
                        className="chip"
                        style={{ marginTop: 6 }}
                        onClick={() => void remove(c)}
                      >
                        {t('feed.comments.delete')}
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {cursor && (
                <button className="btn btn--ghost btn--small" disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? <span className="spinner" /> : t('feed.comments.loadMore')}
                </button>
              )}
            </div>
          )}
        </div>

        {notice && (
          <div
            className={`banner ${notice.tone === 'danger' ? 'banner--danger' : 'banner--info'} small`}
            style={{ margin: '0 16px 8px' }}
          >
            {notice.text}
          </div>
        )}

        <div
          className="row"
          style={{ padding: '10px 16px 14px', borderTop: '1px solid var(--border)', gap: 8 }}
        >
          <input
            ref={inputRef}
            className="input"
            style={{ flex: 1 }}
            value={draft}
            maxLength={TEXT_MAX}
            placeholder={t('feed.comments.placeholder')}
            aria-label={t('feed.comments.placeholder')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
          />
          <button
            className="btn btn--primary btn--small"
            disabled={posting || draft.trim().length === 0}
            onClick={() => void submit()}
          >
            {posting ? <span className="spinner" /> : t('feed.comments.post')}
          </button>
        </div>
        {draft.length >= COUNTER_FROM && (
          <div className="tiny faint" style={{ padding: '0 16px 10px', textAlign: 'right' }}>
            {draft.length}/{TEXT_MAX}
          </div>
        )}
      </div>
    </div>
  );
}
